import React, { useState, useEffect, useCallback } from 'react';
import { getApiBaseUrl } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Building2, CreditCard, CheckCircle, Clock, AlertCircle,
  ArrowUpRight, ArrowDownLeft, RefreshCw, Shield, Wallet,
  Eye, Copy, Home, TrendingUp, TrendingDown, Calendar,
  ChevronRight, Banknote, PiggyBank, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const API_URL = getApiBaseUrl();

const LandlordFinancialDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [payoutPreview, setPayoutPreview] = useState(null);
  const [payoutHistory, setPayoutHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [propertyDetail, setPropertyDetail] = useState(null);

  const fetchDashboard = useCallback(async () => {
    if (!user?.id) return;
    
    const token = localStorage.getItem('access_token') || localStorage.getItem('token');
    try {
      // For admin users, we need to provide a landlord_id
      // For now, show an empty dashboard for admins (they can select landlord)
      const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
      
      if (isAdmin) {
        // Admin viewing - show summary
        setDashboard({
          summary: {
            total_pending_payout: "0.00",
            total_pending_collection: "0.00",
            total_in_arrears: "0.00",
            total_griffin_balance: "0.00",
            property_count: 0
          },
          properties: [],
          is_admin_view: true
        });
        return;
      }
      
      const response = await fetch(`${API_URL}/api/fiscal/landlord/dashboard`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDashboard(data);
      }
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    }
  }, [user?.id, user?.role]);

  const fetchPayoutPreview = useCallback(async () => {
    const token = localStorage.getItem('access_token') || localStorage.getItem('token');
    const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
    
    if (isAdmin) {
      setPayoutPreview(null);
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/fiscal/payout/preview`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPayoutPreview(data);
      }
    } catch (error) {
      console.error('Error fetching payout preview:', error);
    }
  }, [user?.role]);

  const fetchPayoutHistory = useCallback(async () => {
    const token = localStorage.getItem('access_token') || localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/fiscal/payout/history`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPayoutHistory(data.payouts || []);
      }
    } catch (error) {
      console.error('Error fetching payout history:', error);
    }
  }, []);

  const fetchPropertyDetail = async (propertyId) => {
    const token = localStorage.getItem('access_token') || localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/fiscal/landlord/property/${propertyId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPropertyDetail(data);
        setSelectedProperty(propertyId);
      }
    } catch (error) {
      console.error('Error fetching property detail:', error);
      toast.error('Failed to load property details');
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchDashboard(), fetchPayoutPreview(), fetchPayoutHistory()]);
      setLoading(false);
    };
    loadData();
  }, [fetchDashboard, fetchPayoutPreview, fetchPayoutHistory]);

  const formatCurrency = (amount) => {
    const num = parseFloat(amount) || 0;
    return `£${num.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  const summary = dashboard?.summary || {};
  const properties = dashboard?.properties || [];

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="landlord-financial-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Wallet className="w-6 h-6 text-blue-600" />
            Financial Dashboard
          </h1>
          <p className="text-gray-500 mt-1">Overview of your rental income, payouts, and arrears</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => {
          fetchDashboard();
          fetchPayoutPreview();
        }}>
          <RefreshCw className="w-4 h-4 mr-1" />
          Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Pending Payout */}
        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-green-700 flex items-center gap-2">
              <PiggyBank className="w-4 h-4" />
              Pending Payout
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-800">
              {formatCurrency(summary.total_pending_payout)}
            </div>
            <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
              <CheckCircle className="w-3 h-3" />
              Collected, awaiting transfer
            </p>
          </CardContent>
        </Card>

        {/* Pending Collection */}
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-blue-700 flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Pending Collection
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-800">
              {formatCurrency(summary.total_pending_collection)}
            </div>
            <p className="text-xs text-blue-600 mt-1 flex items-center gap-1">
              <Clock className="w-3 h-3" />
              Due this month
            </p>
          </CardContent>
        </Card>

        {/* In Arrears */}
        <Card className={`bg-gradient-to-br ${parseFloat(summary.total_in_arrears) > 0 ? 'from-red-50 to-red-100 border-red-200' : 'from-gray-50 to-gray-100 border-gray-200'}`}>
          <CardHeader className="pb-2">
            <CardTitle className={`text-sm font-medium flex items-center gap-2 ${parseFloat(summary.total_in_arrears) > 0 ? 'text-red-700' : 'text-gray-700'}`}>
              <AlertTriangle className="w-4 h-4" />
              In Arrears
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${parseFloat(summary.total_in_arrears) > 0 ? 'text-red-800' : 'text-gray-800'}`}>
              {formatCurrency(summary.total_in_arrears)}
            </div>
            <p className={`text-xs mt-1 ${parseFloat(summary.total_in_arrears) > 0 ? 'text-red-600' : 'text-gray-600'}`}>
              {parseFloat(summary.total_in_arrears) > 0 ? 'Overdue payments' : 'No overdue payments'}
            </p>
          </CardContent>
        </Card>

        {/* Griffin Balance */}
        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-purple-700 flex items-center gap-2">
              <Building2 className="w-4 h-4" />
              Client Account Balance
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-800">
              {formatCurrency(summary.total_griffin_balance)}
            </div>
            <p className="text-xs text-purple-600 mt-1">
              {summary.property_count || 0} properties
            </p>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3 mb-6">
          <TabsTrigger value="overview" className="flex items-center gap-2">
            <Home className="w-4 h-4" />
            Properties
          </TabsTrigger>
          <TabsTrigger value="payout" className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4" />
            Next Payout
          </TabsTrigger>
          <TabsTrigger value="history" className="flex items-center gap-2">
            <CreditCard className="w-4 h-4" />
            Payout History
          </TabsTrigger>
        </TabsList>

        {/* Properties Tab */}
        <TabsContent value="overview">
          <Card>
            <CardHeader>
              <CardTitle>Property Financial Overview</CardTitle>
              <CardDescription>Click on a property to view detailed financial information</CardDescription>
            </CardHeader>
            <CardContent>
              {properties.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Home className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No properties with active tenancies</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {properties.map((property) => (
                    <div
                      key={property.property_id}
                      className="p-4 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => fetchPropertyDetail(property.property_id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <Home className="w-4 h-4 text-gray-400" />
                            <span className="font-medium">{property.address}</span>
                            <Badge variant="outline" className="text-xs">
                              {property.management_type}
                            </Badge>
                          </div>
                          <p className="text-sm text-gray-500 mt-1">
                            Monthly rent: {formatCurrency(property.monthly_rent)}
                          </p>
                        </div>
                        
                        <div className="flex items-center gap-6 text-sm">
                          <div className="text-right">
                            <p className="text-gray-500">Pending Payout</p>
                            <p className="font-semibold text-green-600">{formatCurrency(property.pending_payout)}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-gray-500">Pending Collection</p>
                            <p className="font-semibold text-blue-600">{formatCurrency(property.pending_collection)}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-gray-500">Arrears</p>
                            <p className={`font-semibold ${parseFloat(property.arrears) > 0 ? 'text-red-600' : 'text-gray-400'}`}>
                              {formatCurrency(property.arrears)}
                            </p>
                          </div>
                          <ChevronRight className="w-5 h-5 text-gray-400" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Property Detail Modal/Drawer would go here */}
          {selectedProperty && propertyDetail && (
            <Card className="mt-4 border-2 border-blue-200">
              <CardHeader className="bg-blue-50">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">
                    {propertyDetail.property?.address}
                  </CardTitle>
                  <Button variant="ghost" size="sm" onClick={() => setSelectedProperty(null)}>
                    ✕
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4 pt-4">
                {/* Griffin Account */}
                {propertyDetail.griffin_account && (
                  <div className="p-4 bg-purple-50 rounded-lg">
                    <h4 className="font-medium text-purple-900 mb-2">Client Money Account</h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">Sort Code:</span>
                        <span className="ml-2 font-mono">{propertyDetail.griffin_account.sort_code}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Account:</span>
                        <span className="ml-2 font-mono">{propertyDetail.griffin_account.account_number}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Balance:</span>
                        <span className="ml-2 font-semibold">{formatCurrency(propertyDetail.griffin_account.balance)}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Status:</span>
                        <Badge className="ml-2" variant="outline">{propertyDetail.griffin_account.status}</Badge>
                      </div>
                    </div>
                  </div>
                )}

                {/* Recent Payments */}
                {propertyDetail.payments?.length > 0 && (
                  <div>
                    <h4 className="font-medium mb-2">Recent Payments</h4>
                    <div className="space-y-2">
                      {propertyDetail.payments.slice(0, 5).map((payment) => (
                        <div key={payment.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                          <div className="flex items-center gap-2">
                            <ArrowDownLeft className="w-4 h-4 text-green-500" />
                            <span className="text-sm">{payment.type}</span>
                            <span className="text-xs text-gray-500">
                              {new Date(payment.date).toLocaleDateString('en-GB')}
                            </span>
                          </div>
                          <span className="font-medium text-green-600">{formatCurrency(payment.amount)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Rent Schedule */}
                {propertyDetail.rent_schedule?.length > 0 && (
                  <div>
                    <h4 className="font-medium mb-2">Rent Schedule</h4>
                    <div className="space-y-2">
                      {propertyDetail.rent_schedule.slice(0, 6).map((item) => (
                        <div key={item.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                          <div>
                            <span className="text-sm">{new Date(item.due_date).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })}</span>
                          </div>
                          <div className="flex items-center gap-4">
                            <span className="text-sm">{formatCurrency(item.amount)}</span>
                            <Badge className={
                              item.status === 'paid' ? 'bg-green-100 text-green-800' :
                              item.status === 'overdue' ? 'bg-red-100 text-red-800' :
                              item.status === 'partial' ? 'bg-amber-100 text-amber-800' :
                              'bg-gray-100 text-gray-800'
                            }>
                              {item.status}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Payout Preview Tab */}
        <TabsContent value="payout">
          <Card>
            <CardHeader>
              <CardTitle>Next Payout Preview</CardTitle>
              <CardDescription>
                {payoutPreview?.next_payout_date && (
                  <span className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Expected payout date: {new Date(payoutPreview.next_payout_date).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                  </span>
                )}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {payoutPreview?.summary ? (
                <div className="space-y-6">
                  {/* Summary */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-gray-50 rounded-lg">
                    <div>
                      <p className="text-sm text-gray-500">Gross Rent</p>
                      <p className="text-xl font-bold">{formatCurrency(payoutPreview.summary.total_rent_collected)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Management Fee</p>
                      <p className="text-xl font-bold text-red-600">-{formatCurrency(payoutPreview.summary.total_management_fee)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Maintenance</p>
                      <p className="text-xl font-bold text-red-600">-{formatCurrency(payoutPreview.summary.total_maintenance)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Net Payout</p>
                      <p className="text-xl font-bold text-green-600">{formatCurrency(payoutPreview.summary.total_net_payout)}</p>
                    </div>
                  </div>

                  {/* Property Breakdown */}
                  {payoutPreview.properties?.length > 0 && (
                    <div>
                      <h4 className="font-medium mb-3">Breakdown by Property</h4>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-100">
                            <tr>
                              <th className="text-left p-2">Property</th>
                              <th className="text-right p-2">Rent</th>
                              <th className="text-right p-2">Fee %</th>
                              <th className="text-right p-2">Fee</th>
                              <th className="text-right p-2">Maintenance</th>
                              <th className="text-right p-2 font-semibold">Net</th>
                            </tr>
                          </thead>
                          <tbody>
                            {payoutPreview.properties.map((prop) => (
                              <tr key={prop.property_id} className="border-b">
                                <td className="p-2">{prop.address}</td>
                                <td className="text-right p-2">{formatCurrency(prop.rent_collected)}</td>
                                <td className="text-right p-2">{prop.management_fee_percent}%</td>
                                <td className="text-right p-2 text-red-600">-{formatCurrency(prop.management_fee)}</td>
                                <td className="text-right p-2 text-red-600">-{formatCurrency(prop.maintenance_deductions)}</td>
                                <td className="text-right p-2 font-semibold text-green-600">{formatCurrency(prop.net_payout)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <PiggyBank className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No payout data available for this period</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Payout History Tab */}
        <TabsContent value="history">
          <Card>
            <CardHeader>
              <CardTitle>Payout History</CardTitle>
              <CardDescription>Your past 12 months of payouts</CardDescription>
            </CardHeader>
            <CardContent>
              {payoutHistory.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <CreditCard className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No payout history yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {payoutHistory.map((payout) => (
                    <div key={payout.id} className="p-4 border rounded-lg">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">
                            {new Date(payout.period_start).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })}
                          </p>
                          <p className="text-sm text-gray-500">
                            {payout.period_start} to {payout.period_end}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-green-600">{formatCurrency(payout.net_payout)}</p>
                          <Badge className={
                            payout.status === 'completed' ? 'bg-green-100 text-green-800' :
                            payout.status === 'processing' ? 'bg-blue-100 text-blue-800' :
                            payout.status === 'failed' ? 'bg-red-100 text-red-800' :
                            'bg-gray-100 text-gray-800'
                          }>
                            {payout.status}
                          </Badge>
                        </div>
                      </div>
                      <div className="mt-2 pt-2 border-t grid grid-cols-4 gap-2 text-sm text-gray-500">
                        <div>Gross: {formatCurrency(payout.gross_rent_collected)}</div>
                        <div>Fee: -{formatCurrency(payout.management_fee)}</div>
                        <div>Maint: -{formatCurrency(payout.maintenance_deductions)}</div>
                        <div>Interest: +{formatCurrency(payout.interest_earned)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default LandlordFinancialDashboard;
